import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import classnames from 'classnames';
import Table from 'reactstrap/lib/Table';
import { useTableSort, SORT_ORDER } from 'site-modules/shared/hooks/editorial/use-table-sort';
import { CmsEntities } from 'client/data/models/cms';
import { getValidImageUrl } from 'client/utils/image-helpers';
import { Link } from 'site-modules/shared/components/link/link';

import './dynamic-leaderboard-table.scss';

export function DynamicLeaderboardTable({ entry, isMobile }) {
  const [tableVehicles, setTableVehicles] = useState(
    entry
      .child('vehicles')
      .children()
      .map(vehicleEntry => ({
        id: vehicleEntry.id,
        ...vehicleEntry.getAllMetadata(),
      }))
  );

  const [sortColumn, sortOrder, onColumnSort] = useTableSort({
    rowValues: tableVehicles,
    setRowValues: setTableVehicles,
  });

  const leaderboardId = entry.metadata('filePath').value().split('/').pop();
  const config = entry.child('leaderboard-config');
  const title = config.metadata('title').value();
  const images = config.links();
  const logo = images.find(({ rel }) => rel === 'logo');
  const titleImage = images.find(({ rel }) => rel === 'title-image');

  const tableDataConfig = config
    .children()
    .map(metricEntry => ({ key: metricEntry.id, name: metricEntry.metadata('metricName').value() }))
    .filter(({ name }) => name);

  return (
    <div className="dynamic-leaderboard-table medium" data-tracking-parent={`${leaderboardId}-table`}>
      {!!titleImage && (
        <div className="mb-0_5">
          <img
            className="title-image"
            src={getValidImageUrl(titleImage.href)}
            alt={titleImage.title || ''}
            {...(isMobile ? { width: '100%' } : { height: '80px' })}
          />
        </div>
      )}
      {!!logo && (
        <div className="mb-0_5">
          <img src={getValidImageUrl(logo.href)} alt={logo.title || ''} height={18} />
        </div>
      )}
      {!!title && <h2 className="heading-3 mb-1_5">{title}</h2>}
      <Table responsive bordered>
        <thead className="bg-cool-gray-90">
          <tr>
            <th scope="col" className="size-16 text-center align-middle bg-blue-90">
              Vehicle
            </th>
            {tableDataConfig.map(({ name, key }) => {
              const valueKey = `${key}Value`;

              return (
                <th
                  key={key}
                  scope="col"
                  aria-sort={sortColumn === valueKey ? sortOrder : SORT_ORDER.NONE}
                  className="text-center align-middle"
                >
                  <button
                    className="sort-btn py-0 ps-0 pe-1_25 pos-r border-0 text-gray-darker fw-bold bg-transparent"
                    onClick={onColumnSort}
                    data-key={valueKey}
                    data-tracking-id="sort_table"
                  >
                    {name}
                    <i className="arrow icon-arrow-up5" aria-hidden />
                    <i className="arrow icon-arrow-down5" aria-hidden />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {tableVehicles.map(({ id, href, vehicleName, ...metrics }) => (
            <tr key={id}>
              <th scope="row">
                <Link
                  to={href}
                  className="fw-normal text-primary-darker text-underline"
                  data-tracking-id="go_to_review"
                >
                  {vehicleName}
                </Link>
              </th>
              {tableDataConfig.map(({ key }) => (
                <td key={`${id}-${key}`} className="text-center">
                  {get(metrics, key, '-')}
                  {!!metrics?.[`${key}Calculation`] && (
                    <div className={classnames('calculation', { red: metrics[`${key}CalculationValue`] < 0 })}>
                      ({metrics[`${key}CalculationValue`] > 0 && '+'}
                      {metrics[`${key}Calculation`]})
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

DynamicLeaderboardTable.propTypes = {
  entry: CmsEntities.Content.isRequired,
  isMobile: PropTypes.bool,
};

DynamicLeaderboardTable.defaultProps = {
  isMobile: false,
};
